import React, { useEffect, useState } from 'react'
import { css } from '@emotion/css'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { usePopper } from 'react-popper'
import { Placement } from '../popper'
import { Arrow, largeArrowWithBorderHeight } from '../popper-arrow'

interface Props<T extends HTMLElement> {
  readonly isOpen: boolean
  readonly renderTarget: (props: {
    readonly getTargetProps: () => {
      readonly ref: React.Ref<T>
    }
  }) => React.ReactElement<any>
  readonly renderContent: () => React.ReactElement<any>
  readonly placement?: Placement
  readonly close?: boolean
  readonly onClose?: () => void
}

type OptionalHTMLElement = HTMLElement | null

export function Popover<T extends HTMLElement>({
  isOpen,
  renderTarget,
  renderContent,
  placement = 'auto',
  close,
  onClose
}: Props<T>) {
  useEffect(() => {
    if (isOpen && close && onClose) {
      onClose()
    }
  })

  const [referenceElement, setReferenceElement] =
    useState<OptionalHTMLElement>(null)
  const [popperElement, setPopperElement] = useState<OptionalHTMLElement>(null)
  const [arrowElement, setArrowElement] = useState<OptionalHTMLElement>(null)

  const {
    styles,
    attributes: { popper: popperAttributes }
  } = usePopper(referenceElement, popperElement, {
    placement,
    modifiers: [{ name: 'arrow', options: { element: arrowElement } }]
  })

  const actualPlacement =
    popperAttributes && popperAttributes['data-popper-placement']
      ? (popperAttributes['data-popper-placement'] as Placement)
      : undefined

  return (
    <>
      {renderTarget({
        getTargetProps: () => ({ ref: setReferenceElement })
      })}
      {isOpen && (
        <div
          ref={setPopperElement}
          style={styles.popper}
          className={css`
            background-color: ${theme.color.white};
            z-index: 100;
            border: 1px solid ${theme.color.neutral40};
            border-radius: 4px;
            box-shadow: 0 0 3px 0 rgba(191, 191, 191, 0.3);

            &[data-popper-placement*='bottom'],
            &[data-popper-placement*='top'] {
              margin: ${largeArrowWithBorderHeight}px 0;
            }

            &[data-popper-placement*='left'],
            &[data-popper-placement*='right'] {
              margin: 0 ${largeArrowWithBorderHeight}px;
            }
          `}
          {...popperAttributes}
        >
          {renderContent()}
          {actualPlacement && (
            <Arrow
              placement={actualPlacement}
              arrowRef={setArrowElement}
              style={styles.arrow}
              size="large"
              color="white"
              borderColor="neutral40"
            />
          )}
        </div>
      )}
    </>
  )
}
